import googleMapsPointUrl from "utilities/google_maps_point_url";
import { kebabCaseKeys, numberizeValues } from "utilities/object_helpers";
import { reduce, isNil, pick, omit, get, merge, cloneDeep } from "lodash";


function prependKeysFrom(obj, str) {
  return Object.keys(obj).reduce((newObj, key) => {
    const newKey = `${str}${key}`;
    return { ...newObj, [newKey]: obj[key] }
  }, {});
}

function applyDeviationsToLineStyle(style, featureProperties) {
  let resolvedStyle = omit(style, ['track_attribute_deviations', 'surface_attribute_deviations']);

  const width = resolveLineStringPropertiesForWidth(featureProperties);
  const surface = resolveLineStringPropertiesForSurface(featureProperties);

  if (get(style, `track_attribute_deviations.${width}`)) {
    resolvedStyle = merge(resolvedStyle, style.track_attribute_deviations[width]);
  }

  if (get(style, `surface_attribute_deviations.${surface}`)) {
    resolvedStyle = merge(resolvedStyle, style.surface_attribute_deviations[surface]);
  }

  return resolvedStyle;
}

function resolveLineStringPropertiesForWidth(properties) {
  const { use } = properties;

  if (['alley', 'cycleway', 'mountain_bike', 'path', 'footway', 'steps'].includes(use)) {
    return 'narrow';
  }

  return 'wide';
}

function resolveLineStringPropertiesForSurface(properties) {
  const { surface, unpaved } = properties;

  if (surface == 'gravel') return 'gravel';
  if (surface == 'dirt' || unpaved) return 'dirt';
  return 'paved';
}

function lineToLinePaint(lineStyle) {
  const paintProperties = pick(lineStyle, ['color', 'width', 'opacity']);
  if (lineStyle.design === 'dashed') {
    paintProperties.dasharray = [1, 2];
  } else if (lineStyle.design === 'dotted') {
    paintProperties.dasharray = [0, 2];
  }
  return {
    "paint": prependKeysFrom(paintProperties, "line-")
  };
}

function lineToLineLayout(lineStyle) {
  if (lineStyle.design != "dotted") return {};

  return {
    "layout": {
      "line-cap": "round"
    }
  };
}

function markerToCircleMarkerPaint(markerStyle) {
  return {
    "paint": prependKeysFrom(kebabCaseKeys(markerStyle), "circle-")
  };
}

function elevationPolygonsToPaint(elevationPolygonsStyle) {
  const coercedStyle = numberizeValues(elevationPolygonsStyle);
  return {
    "paint": prependKeysFrom(coercedStyle, "fill-extrusion-")
  };
}

const popupTranslations = {
  marker_notes: "Notes",
  marker_crew: "Crew access",
  marker_pacer: "Pacer available",
  marker_cutoff: "Cutoff",
  marker_distance: "Distance",
  marker_drop_bag: "Drop bag"
};
const popupKeyOrder = [
  'marker_title',
  'marker_distance',
  'marker_notes',
  'marker_crew',
  'marker_pacer',
  'marker_drop_bag',
  'marker_cutoff'
];

function pointFeatureToPopupContent(pointFeature, { excludeCoordinateLink = false } = {}) {
  const attributes = pointFeature.properties.form;
  const contentMapping = reduce(attributes, function (result, value, key) {
    if (key == 'marker_title')
      result[key] = `<div><strong>${value}</strong></div>`;
    else if (!isNil(value))
      result[key] = `<div><i>${popupTranslations[key]}:</i> ${value}</div>`;

    return result;
  }, {});

  const content = reduce(popupKeyOrder, function (html, key) {
    if (!contentMapping[key]) return html;

    return html + contentMapping[key];
  }, '');

  if (excludeCoordinateLink) return content.trim();

  const googleMapsLink = `
    <div style='float: right'>
      <a href=${googleMapsPointUrl(pointFeature.geometry.coordinates)} target="_blank">
        [${pointFeature.geometry.coordinates[1]}, ${pointFeature.geometry.coordinates[0]}]
      </a>
    </div>
  `;
  return (content + googleMapsLink).trim();
}

export {
  lineToLinePaint,
  lineToLineLayout,
  markerToCircleMarkerPaint,
  elevationPolygonsToPaint,
  pointFeatureToPopupContent,
  applyDeviationsToLineStyle
};
